import { useEffect, useState } from 'react';

export interface CdxIdentity {
  displayName: string;
  email: string;
  exp?: number;
  jti?: string;
}

/** Read window.CDX_IDENTITY (set by bootAuth in index.html before React mount). */
function read(): CdxIdentity | null {
  if (typeof window === 'undefined') return null;
  const id = (window as unknown as { CDX_IDENTITY?: Partial<CdxIdentity> }).CDX_IDENTITY;
  if (!id || !id.email) return null;
  return {
    displayName: id.displayName ?? id.email,
    email: id.email,
    ...(id.exp !== undefined ? { exp: id.exp } : {}),
    ...(id.jti !== undefined ? { jti: id.jti } : {}),
  };
}

/**
 * Subscribe to window.CDX_IDENTITY — populated by bootAuth before React
 * mounts. Falls back to local-dev sentinel when bootAuth bypasses on
 * localhost.
 *
 * AuthRoute waits for bootAuth before rendering children, so by the time a
 * route component mounts, CDX_IDENTITY is reliably set. But Header sometimes
 * renders briefly during the AuthRoute wait — poll once at mount to catch
 * the late-arriving identity.
 */
export function useCdxIdentity(): CdxIdentity | null {
  const [identity, setIdentity] = useState<CdxIdentity | null>(() => read());
  useEffect(() => {
    if (identity) return;
    const interval = setInterval(() => {
      const id = read();
      if (id) {
        setIdentity(id);
        clearInterval(interval);
      }
    }, 100);
    const stop = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [identity]);
  return identity;
}

/**
 * Compute 2-letter initials from a display name.
 *
 * Handles common forms:
 *   "Faidi, William"   → "FW"   (Last, First)
 *   "William Faidi"    → "WF"   (First Last)
 *   "Jane Ops"         → "JO"
 *   "alex.bol@..."     → "AB"   (email local-part dot-split)
 *   "Cher"             → "CH"
 *   ""                 → "??"
 */
export function initialsOf(displayName: string | undefined | null): string {
  const s = (displayName ?? '').trim();
  if (!s) return '??';
  // "Last, First" → swap to First Last so initials read first-then-last
  let parts: string[];
  if (s.includes(',')) {
    const [last, first] = s.split(',').map((p) => p.trim());
    parts = [first || '', last || ''].filter(Boolean);
  } else if (s.includes('@')) {
    const local = s.split('@')[0] ?? '';
    parts = local.split(/[._-]/).filter(Boolean);
  } else {
    parts = s.split(/\s+/).filter(Boolean);
  }
  if (parts.length === 0) return s.slice(0, 2).toUpperCase();
  if (parts.length === 1) {
    const p = parts[0] ?? '';
    return (p.slice(0, 2) || '?').toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] || '') + (last[0] || '')).toUpperCase() || '??';
}

/**
 * Re-format a "Last, First" displayName as "First Last" for friendly display.
 * Leaves other formats alone.
 */
export function friendlyDisplayName(displayName: string | undefined | null): string {
  const s = (displayName ?? '').trim();
  if (!s) return '';
  if (!s.includes(',')) return s;
  const [last, first] = s.split(',').map((p) => p.trim());
  return `${first ?? ''} ${last ?? ''}`.trim();
}
